/**
 * ImpactTimeSystem — cinematic slow-motion on high-impact events.
 *
 * When significant damage is dealt or received, the game simulation
 * slows to ~0.25× for a brief moment. The camera continues at normal
 * speed (it already uses raw delta) so it smoothly pans to the action
 * while everything else is in slow-mo.
 *
 * Importance is computed per event; only events above a threshold trigger
 * slow-mo. Multiple events extend / refresh the effect — they don't stack
 * the time scale lower than the floor.
 *
 * Connected via EventBus — no hard coupling to entity classes.
 */

import { EventBus, GameEvents } from '@/events/EventBus';
import { SmartCamera } from '@/systems/SmartCamera';
import { EnemyType } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';

// ── Tuning ──

/** Minimum importance score (0-1) to trigger slow-mo */
const TRIGGER_THRESHOLD = 0.35;

/** Time scale floor during slow-mo */
const SLOWMO_SCALE = 0.25;

/** How long (seconds, real-time) the slow-mo holds at the floor */
const HOLD_DURATION = 0.30;

/** How long (seconds, real-time) the ramp-back-to-normal takes */
const RAMP_DURATION = 0.40;

/** Cooldown (seconds, real-time) before another slow-mo can trigger */
const COOLDOWN = 1.2;

/** Hotspot weight injected into SmartCamera so it pans to the impact */
const CAMERA_HOTSPOT_WEIGHT = 12.0;

// ── Importance weights per event type ──

/** Enemy destroyed: importance = maxHp / reference HP */
const REF_ENEMY_HP = 300; // a tanky enemy; grunts are ~60

/** Importance bonus for specific enemy types being killed */
const ENEMY_TYPE_BONUS: Partial<Record<EnemyType, number>> = {
  [EnemyType.TANK]: 0.15,
  [EnemyType.BRUTE]: 0.25,
  [EnemyType.BOMBER]: 0.20,
};

/** Robot destroyed = always important */
const ROBOT_DESTROYED_IMPORTANCE = 0.6;

/** Player base damaged: low importance — only triggers slow-mo on very heavy hits */
const BASE_DAMAGE_IMPORTANCE = 0.2;

/** Enemy base destroyed = always big */
const ENEMY_BASE_DESTROYED_IMPORTANCE = 0.8;

/** Critical hit importance (flat) */
const CRITICAL_HIT_IMPORTANCE = 0.25;

/** Mine exploded importance */
const MINE_EXPLODED_IMPORTANCE = 0.45;

/** Flying robot crashing = high importance (dramatic event) */
const ROBOT_CRASHING_IMPORTANCE = 0.7;

// ── State machine ──
enum Phase { IDLE, HOLD, RAMP }

class ImpactTimeSystemClass {
  private phase: Phase = Phase.IDLE;
  /** Seconds remaining in current phase (real-time, not scaled) */
  private timer = 0;
  private cooldownTimer = 0;
  /** The user's chosen speed (1/2/4×) to restore after slow-mo */
  private userSpeed = 1;
  /** Reference to SmartCamera for injecting hotspots */
  private smartCamera: SmartCamera | null = null;

  // ── Event handlers (bound for cleanup) ──

  private onEnemyDestroyed = (enemy: {
    body?: { x: number; y: number };
    maxHp?: number;
    enemyType?: EnemyType;
  }) => {
    const hp = enemy.maxHp ?? 60;
    let importance = Math.min(1, hp / REF_ENEMY_HP);
    if (enemy.enemyType && ENEMY_TYPE_BONUS[enemy.enemyType]) {
      importance += ENEMY_TYPE_BONUS[enemy.enemyType]!;
    }
    const x = enemy.body?.x ?? 0;
    const y = enemy.body?.y ?? 0;
    this.tryTrigger(importance, x, y);
  };

  private onRobotDestroyed = (robot: { x?: number; y?: number }) => {
    this.tryTrigger(ROBOT_DESTROYED_IMPORTANCE, robot.x ?? 0, robot.y ?? 0);
  };

  private onBaseDamaged = (data: { hp: number; x: number; y: number }) => {
    this.tryTrigger(BASE_DAMAGE_IMPORTANCE, data.x, data.y);
  };

  private onEnemyBaseDestroyed = (data: { position?: { x: number; y: number } }) => {
    this.tryTrigger(ENEMY_BASE_DESTROYED_IMPORTANCE, data.position?.x ?? 0, data.position?.y ?? 0);
  };

  private onCriticalHit = (data: { x?: number; y?: number }) => {
    this.tryTrigger(CRITICAL_HIT_IMPORTANCE, data.x ?? 0, data.y ?? 0);
  };

  private onMineExploded = (data: { x?: number; y?: number }) => {
    this.tryTrigger(MINE_EXPLODED_IMPORTANCE, data.x ?? 0, data.y ?? 0);
  };

  private onRobotCrashing = (robot: { x?: number; y?: number }) => {
    this.tryTrigger(ROBOT_CRASHING_IMPORTANCE, robot.x ?? 0, robot.y ?? 0);
  };

  // ── Public API ──

  /** Call once from GameScene.create() */
  init(smartCamera: SmartCamera): void {
    this.smartCamera = smartCamera;
    this.phase = Phase.IDLE;
    this.timer = 0;
    this.cooldownTimer = 0;
    this.userSpeed = 1;

    EventBus.on(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed);
    EventBus.on(GameEvents.ROBOT_DESTROYED, this.onRobotDestroyed);
    EventBus.on(GameEvents.BASE_DAMAGED, this.onBaseDamaged);
    EventBus.on(GameEvents.ENEMY_BASE_DESTROYED, this.onEnemyBaseDestroyed);
    EventBus.on(GameEvents.CRITICAL_HIT, this.onCriticalHit);
    EventBus.on(GameEvents.MINE_EXPLODED, this.onMineExploded);
    EventBus.on(GameEvents.ROBOT_CRASHING, this.onRobotCrashing);
  }

  /** Call from GameScene.shutdown() */
  shutdown(): void {
    EventBus.off(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed);
    EventBus.off(GameEvents.ROBOT_DESTROYED, this.onRobotDestroyed);
    EventBus.off(GameEvents.BASE_DAMAGED, this.onBaseDamaged);
    EventBus.off(GameEvents.ENEMY_BASE_DESTROYED, this.onEnemyBaseDestroyed);
    EventBus.off(GameEvents.CRITICAL_HIT, this.onCriticalHit);
    EventBus.off(GameEvents.MINE_EXPLODED, this.onMineExploded);
    EventBus.off(GameEvents.ROBOT_CRASHING, this.onRobotCrashing);
    this.smartCamera = null;
    this.phase = Phase.IDLE;
  }

  /**
   * Update the time system. Called every frame with RAW delta (not scaled).
   * Returns the effective gameSpeed to apply this frame.
   *
   * @param rawDeltaMs  Raw frame delta in ms
   * @param userSpeed   The speed the player has chosen (1/2/4)
   */
  update(rawDeltaMs: number, userSpeed: number): number {
    this.userSpeed = userSpeed;
    const dt = rawDeltaMs / 1000;

    // Tick cooldown
    if (this.cooldownTimer > 0) {
      this.cooldownTimer -= dt;
    }

    switch (this.phase) {
      case Phase.IDLE:
        return userSpeed;

      case Phase.HOLD:
        this.timer -= dt;
        if (this.timer <= 0) {
          this.phase = Phase.RAMP;
          this.timer = RAMP_DURATION;
        }
        return SLOWMO_SCALE;

      case Phase.RAMP: {
        this.timer -= dt;
        if (this.timer <= 0) {
          this.phase = Phase.IDLE;
          this.cooldownTimer = COOLDOWN;
          return userSpeed;
        }
        // Smooth ease-out ramp from SLOWMO_SCALE back to userSpeed
        const t = 1 - this.timer / RAMP_DURATION; // 0→1
        const eased = t * t; // quadratic ease-in (feels like deceleration of slow-mo)
        return SLOWMO_SCALE + (userSpeed - SLOWMO_SCALE) * eased;
      }
    }
  }

  /** Whether slow-mo is currently active (hold or ramp phase) */
  get active(): boolean {
    return this.phase !== Phase.IDLE;
  }

  /** Current effect intensity 0 (idle) → 1 (full hold). Ramps down during RAMP phase. */
  get intensity(): number {
    switch (this.phase) {
      case Phase.IDLE: return 0;
      case Phase.HOLD: return 1;
      case Phase.RAMP: return Math.max(0, this.timer / RAMP_DURATION);
    }
  }

  // ── Internal ──

  private tryTrigger(importance: number, x?: number, y?: number): void {
    if (!GOD.impactSlowmo) return;
    if (importance < TRIGGER_THRESHOLD) return;

    // If already in slow-mo, refresh/extend the hold
    if (this.phase === Phase.HOLD) {
      this.timer = Math.max(this.timer, HOLD_DURATION * importance);
      // Still add camera hotspot for the new event
      if (x !== undefined && y !== undefined) {
        this.injectCameraHotspot(x, y, importance);
      }
      return;
    }

    // If ramping back, re-enter hold only for very important events
    if (this.phase === Phase.RAMP && importance < 0.6) return;

    // Respect cooldown (but override for very high importance)
    if (this.cooldownTimer > 0 && importance < 0.7) return;

    // Trigger slow-mo
    this.phase = Phase.HOLD;
    this.timer = HOLD_DURATION * Math.max(1, importance);
    this.cooldownTimer = 0; // reset cooldown when entering

    if (x !== undefined && y !== undefined) {
      this.injectCameraHotspot(x, y, importance);
    }
  }

  private injectCameraHotspot(x: number, y: number, importance: number): void {
    if (!this.smartCamera) return;
    // Use the public addImpactHotspot method — weighted heavily so camera pans there
    this.smartCamera.addImpactHotspot(x, y, CAMERA_HOTSPOT_WEIGHT * importance);
  }
}

export const ImpactTimeSystem = new ImpactTimeSystemClass();
